/**
 * Horizontal scrollable tab bar used by the Charger Management page.
 *
 * Each tab is a `{ key, label, badge?, disabled? }`. The badge is a small
 * coloured dot rendered in the upper-right; use it for "drift detected",
 * "security op in flight", "version mismatch", etc.
 */

import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { OcppTheme } from './OcppTheme';

export interface OcppTabDefinition {
  key: string;
  label: string;
  /** Renders a small dot of the given colour to flag attention. */
  badge?: string | null;
  /** Show a "soon" lock icon and disable taps. */
  disabled?: boolean;
}

export interface OcppTabBarProps {
  tabs: OcppTabDefinition[];
  activeKey: string;
  onSelect: (key: string) => void;
}

const OcppTabBar: React.FC<OcppTabBarProps> = ({ tabs, activeKey, onSelect }) => {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      style={styles.scroll}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <Pressable
            key={tab.key}
            disabled={tab.disabled}
            onPress={() => onSelect(tab.key)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive, disabled: !!tab.disabled }}
            accessibilityLabel={tab.label}
            style={({ pressed }) => [
              styles.tab,
              isActive && styles.tabActive,
              tab.disabled && styles.tabDisabled,
              pressed && !tab.disabled && { opacity: 0.85 },
            ]}
          >
            <Text
              style={[
                styles.tabLabel,
                isActive && styles.tabLabelActive,
                tab.disabled && styles.tabLabelDisabled,
              ]}
            >
              {tab.label}
            </Text>
            {tab.badge ? <View style={[styles.badge, { backgroundColor: tab.badge }]} /> : null}
          </Pressable>
        );
      })}
    </ScrollView>
  );
};

export default OcppTabBar;

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: OcppTheme.bg,
    /** Avoid stretching to fill vertical space when used under a flex column parent. */
    flexGrow: 0,
  },
  scrollContent: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: OcppTheme.surfaceMuted,
    borderWidth: 1,
    borderColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabActive: {
    backgroundColor: OcppTheme.accentDim,
    borderColor: OcppTheme.accent,
  },
  tabDisabled: {
    opacity: 0.5,
  },
  tabLabel: {
    color: OcppTheme.textMuted,
    fontSize: 13,
    fontWeight: '600',
  },
  tabLabelActive: {
    color: OcppTheme.accent,
    fontWeight: '700',
  },
  tabLabelDisabled: {
    color: OcppTheme.textDim,
  },
  badge: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});
